var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef } from "react";
const PRECISIONS = ["lowp", "mediump", "highp"];
const FS_MAIN_SHADER = `\nvoid main(void){
    vec4 color = vec4(0.0,0.0,0.0,1.0);
    mainImage( color, gl_FragCoord.xy );
    gl_FragColor = color;
}`;
const BASIC_FS = `void mainImage( out vec4 fragColor, in vec2 fragCoord ) {
    vec2 uv = fragCoord/iResolution.xy;
    vec3 col = 0.5 + 0.5*cos(iTime+uv.xyx+vec3(0,2,4));
    fragColor = vec4(col,1.0);
}`;
const BASIC_VS = `attribute vec3 aVertexPosition;
void main(void) {
    gl_Position = vec4(aVertexPosition, 1.0);
}`;
const UNIFORM_TIME = "iTime";
const UNIFORM_TIMEDELTA = "iTimeDelta";
const UNIFORM_DATE = "iDate";
const UNIFORM_FRAME = "iFrame";
const UNIFORM_MOUSE = "iMouse";
const UNIFORM_RESOLUTION = "iResolution";
const UNIFORM_CHANNEL = "iChannel";
const UNIFORM_CHANNELRESOLUTION = "iChannelResolution";
const UNIFORM_DEVICEORIENTATION = "iDeviceOrientation";
function isMatrixType(t, v) {
    return t.includes("Matrix") && Array.isArray(v);
}
function isVectorListType(t, v) {
    return (t.includes("v") &&
        Array.isArray(v) &&
        v.length > Number.parseInt(t.charAt(0)));
}
function isVectorType(t, v) {
    return (!t.includes("v") &&
        Array.isArray(v) &&
        v.length > Number.parseInt(t.charAt(0)));
}
const processUniform = (gl, location, t, value) => {
    if (isVectorType(t, value)) {
        switch (t) {
            case "2f":
                return gl.uniform2f(location, value[0], value[1]);
            case "3f":
                return gl.uniform3f(location, value[0], value[1], value[2]);
            case "4f":
                return gl.uniform4f(location, value[0], value[1], value[2], value[3]);
            case "2i":
                return gl.uniform2i(location, value[0], value[1]);
            case "3i":
                return gl.uniform3i(location, value[0], value[1], value[2]);
            case "4i":
                return gl.uniform4i(location, value[0], value[1], value[2], value[3]);
        }
    }
    if (typeof value === "number") {
        switch (t) {
            case "1i":
                return gl.uniform1i(location, value);
            default:
                return gl.uniform1f(location, value);
        }
    }
    switch (t) {
        case "1iv":
            return gl.uniform1iv(location, value);
        case "2iv":
            return gl.uniform2iv(location, value);
        case "3iv":
            return gl.uniform3iv(location, value);
        case "4iv":
            return gl.uniform4iv(location, value);
        case "1fv":
            return gl.uniform1fv(location, value);
        case "2fv":
            return gl.uniform2fv(location, value);
        case "3fv":
            return gl.uniform3fv(location, value);
        case "4fv":
            return gl.uniform4fv(location, value);
        case "Matrix2fv":
            return gl.uniformMatrix2fv(location, false, value);
        case "Matrix3fv":
            return gl.uniformMatrix3fv(location, false, value);
        case "Matrix4fv":
            return gl.uniformMatrix4fv(location, false, value);
    }
};
const uniformTypeToGLSLType = (t) => {
    switch (t) {
        case "1f":
            return "float";
        case "2f":
            return "vec2";
        case "3f":
            return "vec3";
        case "4f":
            return "vec4";
        case "1i":
            return "int";
        case "2i":
            return "ivec2";
        case "3i":
            return "ivec3";
        case "4i":
            return "ivec4";
        case "1iv":
            return "int";
        case "2iv":
            return "ivec2";
        case "3iv":
            return "ivec3";
        case "4iv":
            return "ivec4";
        case "1fv":
            return "float";
        case "2fv":
            return "vec2";
        case "3fv":
            return "vec3";
        case "4fv":
            return "vec4";
        case "Matrix2fv":
            return "mat2";
        case "Matrix3fv":
            return "mat3";
        case "Matrix4fv":
            return "mat4";
        default:
            console.error(log(`The uniform type "${t}" is not valid, please make sure your uniform type is valid`));
    }
};
const LinearFilter = 9729;
const NearestFilter = 9728;
const LinearMipMapLinearFilter = 9987;
const ClampToEdgeWrapping = 33071;
const RepeatWrapping = 10497;
class Texture {
    constructor(gl) {
        this.isLoaded = false;
        this.isVideo = false;
        this.flipY = -1;
        this.width = 0;
        this.height = 0;
        this._webglTexture = null;
        this.updateTexture = (texture, video, flipY) => {
            const { gl } = this;
            const level = 0;
            const internalFormat = gl.RGBA;
            const srcFormat = gl.RGBA;
            const srcType = gl.UNSIGNED_BYTE;
            gl.bindTexture(gl.TEXTURE_2D, texture);
            gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, flipY);
            gl.texImage2D(gl.TEXTURE_2D, level, internalFormat, srcFormat, srcType, video);
        };
        this.setupVideo = (url) => {
            const video = document.createElement("video");
            let playing = false;
            let timeupdate = false;
            video.autoplay = true;
            video.muted = true;
            video.loop = true;
            video.crossOrigin = "anonymous";
            const checkReady = () => {
                if (playing && timeupdate) {
                    this.isLoaded = true;
                }
            };
            video.addEventListener("playing", () => {
                playing = true;
                this.width = video.videoWidth || 0;
                this.height = video.videoHeight || 0;
                checkReady();
            }, true);
            video.addEventListener("timeupdate", () => {
                timeupdate = true;
                checkReady();
            }, true);
            video.src = url;
            return video;
        };
        this.makePowerOf2 = (image) => {
            if (image instanceof HTMLImageElement ||
                image instanceof HTMLCanvasElement ||
                image instanceof ImageBitmap) {
                if (this.pow2canvas === undefined)
                    this.pow2canvas = document.createElement("canvas");
                this.pow2canvas.width = 2 ** Math.floor(Math.log(image.width) / Math.LN2);
                this.pow2canvas.height =
                    2 ** Math.floor(Math.log(image.height) / Math.LN2);
                const context = this.pow2canvas.getContext("2d");
                context === null || context === void 0 ? void 0 : context.drawImage(image, 0, 0, this.pow2canvas.width, this.pow2canvas.height);
                console.warn(log(`Image is not power of two ${image.width} x ${image.height}. Resized to ${this.pow2canvas.width} x ${this.pow2canvas.height};`));
                return this.pow2canvas;
            }
            return image;
        };
        this.load = async (textureArgs) => {
            const { gl } = this;
            const { url, wrapS, wrapT, minFilter, magFilter, flipY = -1, } = textureArgs;
            if (!url) {
                return Promise.reject(new Error(log("Missing url, please make sure to pass the url of your texture { url: ... }")));
            }
            const isImage = /(\.jpg|\.jpeg|\.png|\.gif|\.bmp)$/i.exec(url);
            const isVideo = /(\.mp4|\.3gp|\.webm|\.ogv)$/i.exec(url);
            if (isImage === null && isVideo === null) {
                return Promise.reject(new Error(log(`Please upload a video or an image with a valid format (url: ${url})`)));
            }
            Object.assign(this, { url, wrapS, wrapT, minFilter, magFilter, flipY });
            const level = 0;
            const internalFormat = gl.RGBA;
            const width = 1;
            const height = 1;
            const border = 0;
            const srcFormat = gl.RGBA;
            const srcType = gl.UNSIGNED_BYTE;
            const pixel = new Uint8Array([255, 255, 255, 0]);
            const texture = gl.createTexture();
            gl.bindTexture(gl.TEXTURE_2D, texture);
            gl.texImage2D(gl.TEXTURE_2D, level, internalFormat, width, height, border, srcFormat, srcType, pixel);
            if (isVideo) {
                const video = this.setupVideo(url);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
                this._webglTexture = texture;
                this.source = video;
                this.isVideo = true;
                return video.play().then(() => this);
            }
            async function loadImage() {
                return new Promise((resolve, reject) => {
                    const image = new Image();
                    image.crossOrigin = "anonymous";
                    image.onload = () => {
                        resolve(image);
                    };
                    image.onerror = () => {
                        reject(new Error(log(`failed loading url: ${url}`)));
                    };
                    image.src = url !== null && url !== void 0 ? url : "";
                });
            }
            let image = (await loadImage());
            let isPowerOf2 = (image.width & (image.width - 1)) === 0 &&
                (image.height & (image.height - 1)) === 0;
            if ((textureArgs.wrapS !== ClampToEdgeWrapping ||
                textureArgs.wrapT !== ClampToEdgeWrapping ||
                (textureArgs.minFilter !== NearestFilter &&
                    textureArgs.minFilter !== LinearFilter)) &&
                !isPowerOf2) {
                image = this.makePowerOf2(image);
                isPowerOf2 = true;
            }
            gl.bindTexture(gl.TEXTURE_2D, texture);
            gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, flipY);
            gl.texImage2D(gl.TEXTURE_2D, level, internalFormat, srcFormat, srcType, image);
            if (isPowerOf2 &&
                textureArgs.minFilter !== NearestFilter &&
                textureArgs.minFilter !== LinearFilter) {
                gl.generateMipmap(gl.TEXTURE_2D);
            }
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, this.wrapS || RepeatWrapping);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, this.wrapT || RepeatWrapping);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, this.minFilter || LinearMipMapLinearFilter);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, this.magFilter || LinearFilter);
            this._webglTexture = texture;
            this.source = image;
            this.isVideo = false;
            this.isLoaded = true;
            this.width = image.width || 0;
            this.height = image.height || 0;
            return this;
        };
        this.gl = gl;
    }
}
const log = (text) => `react-shaders: ${text}`;
const latestPointerClientCoords = (e) => {
    var _a, _b;
    if ("changedTouches" in e) {
        const t = e.changedTouches[0];
        return [(_a = t === null || t === void 0 ? void 0 : t.clientX) !== null && _a !== void 0 ? _a : 0, (_b = t === null || t === void 0 ? void 0 : t.clientY) !== null && _b !== void 0 ? _b : 0];
    }
    return [e.clientX, e.clientY];
};
const lerpVal = (v0, v1, t) => v0 * (1 - t) + v1 * t;
const insertStringAtIndex = (currentString, string, index) => index > 0
    ? currentString.substring(0, index) +
        string +
        currentString.substring(index, currentString.length)
    : string + currentString;
export function ReactShaderToy(_a) {
    var { fs, vs = BASIC_VS, textures = [], uniforms: propUniforms, clearColor = [0, 0, 0, 1], precision = "highp", style, contextAttributes = {}, lerp = 1, devicePixelRatio = 1, onDoneLoadingTextures, onError = console.error, onWarning = console.warn, animateWhenNotVisible = false } = _a, canvasProps = __rest(_a, ["fs", "vs", "textures", "uniforms", "clearColor", "precision", "style", "contextAttributes", "lerp", "devicePixelRatio", "onDoneLoadingTextures", "onError", "onWarning", "animateWhenNotVisible"]);
    // Refs for WebGL state
    const canvasRef = useRef(null);
    const glRef = useRef(null);
    const squareVerticesBufferRef = useRef(null);
    const shaderProgramRef = useRef(null);
    const vertexPositionAttributeRef = useRef(undefined);
    const animFrameIdRef = useRef(undefined);
    const initFrameIdRef = useRef(undefined);
    const isVisibleRef = useRef(true);
    const animateWhenNotVisibleRef = useRef(animateWhenNotVisible);
    const mousedownRef = useRef(false);
    const canvasPositionRef = useRef(undefined);
    const timerRef = useRef(0);
    const lastMouseArrRef = useRef([0, 0]);
    const texturesArrRef = useRef([]);
    const lastTimeRef = useRef(0);
    const resizeObserverRef = useRef(undefined);
    const uniformsRef = useRef({
        [UNIFORM_TIME]: { type: "float", isNeeded: false, value: 0 },
        [UNIFORM_TIMEDELTA]: { type: "float", isNeeded: false, value: 0 },
        [UNIFORM_DATE]: { type: "vec4", isNeeded: false, value: [0, 0, 0, 0] },
        [UNIFORM_MOUSE]: { type: "vec4", isNeeded: false, value: [0, 0, 0, 0] },
        [UNIFORM_RESOLUTION]: { type: "vec2", isNeeded: false, value: [0, 0] },
        [UNIFORM_FRAME]: { type: "int", isNeeded: false, value: 0 },
        [UNIFORM_DEVICEORIENTATION]: {
            type: "vec4",
            isNeeded: false,
            value: [0, 0, 0, 0],
        },
    });
    const propsUniformsRef = useRef(propUniforms);
    const setupChannelRes = (texture, id) => {
        var _a, _b;
        const width = "width" in texture ? ((_a = texture.width) !== null && _a !== void 0 ? _a : 0) : 0;
        const height = "height" in texture ? ((_b = texture.height) !== null && _b !== void 0 ? _b : 0) : 0;
        const channelResUniform = uniformsRef.current.iChannelResolution;
        if (!channelResUniform)
            return;
        const channelResValue = Array.isArray(channelResUniform.value)
            ? channelResUniform.value
            : (channelResUniform.value = []);
        channelResValue[id * 3] = width * devicePixelRatio;
        channelResValue[id * 3 + 1] = height * devicePixelRatio;
        channelResValue[id * 3 + 2] = 0;
    };
    const initWebGL = () => {
        var _a, _b;
        if (!canvasRef.current)
            return;
        glRef.current = (canvasRef.current.getContext("webgl", contextAttributes) ||
            canvasRef.current.getContext("experimental-webgl", contextAttributes));
        (_a = glRef.current) === null || _a === void 0 ? void 0 : _a.getExtension("OES_standard_derivatives");
        (_b = glRef.current) === null || _b === void 0 ? void 0 : _b.getExtension("EXT_shader_texture_lod");
    };
    const initBuffers = () => {
        var _a;
        const gl = glRef.current;
        squareVerticesBufferRef.current = (_a = gl === null || gl === void 0 ? void 0 : gl.createBuffer()) !== null && _a !== void 0 ? _a : null;
        gl === null || gl === void 0 ? void 0 : gl.bindBuffer(gl.ARRAY_BUFFER, squareVerticesBufferRef.current);
        const vertices = [
            1.0, 1.0, 0.0, -1.0, 1.0, 0.0, 1.0, -1.0, 0.0, -1.0, -1.0, 0.0,
        ];
        gl === null || gl === void 0 ? void 0 : gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(vertices), gl.STATIC_DRAW);
    };
    const onDeviceOrientationChange = ({ alpha, beta, gamma, }) => {
        var _a;
        uniformsRef.current.iDeviceOrientation.value = [
            alpha !== null && alpha !== void 0 ? alpha : 0,
            beta !== null && beta !== void 0 ? beta : 0,
            gamma !== null && gamma !== void 0 ? gamma : 0,
            (_a = window.orientation) !== null && _a !== void 0 ? _a : 0,
        ];
    };
    const mouseDown = (e) => {
        var _a, _b, _c, _d, _e, _f, _g;
        const [clientX = 0, clientY = 0] = latestPointerClientCoords(e);
        const mouseX = clientX - ((_b = (_a = canvasPositionRef.current) === null || _a === void 0 ? void 0 : _a.left) !== null && _b !== void 0 ? _b : 0) - window.pageXOffset;
        const mouseY = ((_d = (_c = canvasPositionRef.current) === null || _c === void 0 ? void 0 : _c.height) !== null && _d !== void 0 ? _d : 0) -
            clientY -
            ((_f = (_e = canvasPositionRef.current) === null || _e === void 0 ? void 0 : _e.top) !== null && _f !== void 0 ? _f : 0) -
            window.pageYOffset;
        mousedownRef.current = true;
        const mouseValue = Array.isArray((_g = uniformsRef.current.iMouse) === null || _g === void 0 ? void 0 : _g.value)
            ? uniformsRef.current.iMouse.value
            : undefined;
        if (mouseValue) {
            mouseValue[2] = mouseX;
            mouseValue[3] = mouseY;
        }
        lastMouseArrRef.current[0] = mouseX;
        lastMouseArrRef.current[1] = mouseY;
    };
    const mouseMove = (e) => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        canvasPositionRef.current = (_a = canvasRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        const [clientX = 0, clientY = 0] = latestPointerClientCoords(e);
        const mouseX = clientX - ((_c = (_b = canvasPositionRef.current) === null || _b === void 0 ? void 0 : _b.left) !== null && _c !== void 0 ? _c : 0);
        const mouseY = ((_e = (_d = canvasPositionRef.current) === null || _d === void 0 ? void 0 : _d.height) !== null && _e !== void 0 ? _e : 0) -
            clientY -
            ((_g = (_f = canvasPositionRef.current) === null || _f === void 0 ? void 0 : _f.top) !== null && _g !== void 0 ? _g : 0);
        if (lerp !== 1) {
            lastMouseArrRef.current[0] = mouseX;
            lastMouseArrRef.current[1] = mouseY;
        }
        else {
            const mouseValue = Array.isArray((_h = uniformsRef.current.iMouse) === null || _h === void 0 ? void 0 : _h.value)
                ? uniformsRef.current.iMouse.value
                : undefined;
            if (mouseValue) {
                mouseValue[0] = mouseX;
                mouseValue[1] = mouseY;
            }
        }
    };
    const mouseUp = () => {
        var _a;
        const mouseValue = Array.isArray((_a = uniformsRef.current.iMouse) === null || _a === void 0 ? void 0 : _a.value)
            ? uniformsRef.current.iMouse.value
            : undefined;
        if (mouseValue) {
            mouseValue[2] = 0;
            mouseValue[3] = 0;
        }
    };
    const onResize = () => {
        var _a, _b, _c, _d, _e, _f;
        const gl = glRef.current;
        if (!gl)
            return;
        canvasPositionRef.current = (_a = canvasRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        // Force pixel ratio to be one to avoid expensive calculus on retina display.
        const realToCSSPixels = devicePixelRatio;
        const displayWidth = Math.floor(((_c = (_b = canvasPositionRef.current) === null || _b === void 0 ? void 0 : _b.width) !== null && _c !== void 0 ? _c : 1) * realToCSSPixels);
        const displayHeight = Math.floor(((_e = (_d = canvasPositionRef.current) === null || _d === void 0 ? void 0 : _d.height) !== null && _e !== void 0 ? _e : 1) * realToCSSPixels);
        gl.canvas.width = displayWidth;
        gl.canvas.height = displayHeight;
        if (((_f = uniformsRef.current.iResolution) === null || _f === void 0 ? void 0 : _f.isNeeded) && shaderProgramRef.current) {
            const rUniform = gl.getUniformLocation(shaderProgramRef.current, UNIFORM_RESOLUTION);
            gl.uniform2fv(rUniform, [gl.canvas.width, gl.canvas.height]);
        }
    };
    const createShader = (type, shaderCodeAsText) => {
        const gl = glRef.current;
        if (!gl)
            return null;
        const shader = gl.createShader(type);
        if (!shader)
            return null;
        gl.shaderSource(shader, shaderCodeAsText);
        gl.compileShader(shader);
        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
            onWarning === null || onWarning === void 0 ? void 0 : onWarning(log(`Error compiling the shader:\n${shaderCodeAsText}`));
            const compilationLog = gl.getShaderInfoLog(shader);
            gl.deleteShader(shader);
            onError === null || onError === void 0 ? void 0 : onError(log(`Shader compiler log: ${compilationLog}`));
        }
        return shader;
    };
    const initShaders = (fragmentShader, vertexShader) => {
        const gl = glRef.current;
        if (!gl)
            return;
        const fragmentShaderObj = createShader(gl.FRAGMENT_SHADER, fragmentShader);
        const vertexShaderObj = createShader(gl.VERTEX_SHADER, vertexShader);
        shaderProgramRef.current = gl.createProgram();
        if (!shaderProgramRef.current || !vertexShaderObj || !fragmentShaderObj)
            return;
        gl.attachShader(shaderProgramRef.current, vertexShaderObj);
        gl.attachShader(shaderProgramRef.current, fragmentShaderObj);
        gl.linkProgram(shaderProgramRef.current);
        if (!gl.getProgramParameter(shaderProgramRef.current, gl.LINK_STATUS)) {
            onError === null || onError === void 0 ? void 0 : onError(log(`Unable to initialize the shader program: ${gl.getProgramInfoLog(shaderProgramRef.current)}`));
            return;
        }
        gl.useProgram(shaderProgramRef.current);
        vertexPositionAttributeRef.current = gl.getAttribLocation(shaderProgramRef.current, "aVertexPosition");
        gl.enableVertexAttribArray(vertexPositionAttributeRef.current);
    };
    const processCustomUniforms = () => {
        if (propUniforms) {
            for (const name of Object.keys(propUniforms)) {
                const uniform = propUniforms[name];
                if (!uniform)
                    continue;
                const { value, type } = uniform;
                const glslType = uniformTypeToGLSLType(type);
                if (!glslType)
                    continue;
                const tempObject = {};
                if (isMatrixType(type, value)) {
                    const arrayLength = type.length;
                    const val = Number.parseInt(type.charAt(arrayLength - 3));
                    const numberOfMatrices = Math.floor(value.length / (val * val));
                    if (value.length > val * val)
                        tempObject.arraySize = `[${numberOfMatrices}]`;
                }
                else if (isVectorListType(type, value)) {
                    tempObject.arraySize = `[${Math.floor(value.length / Number.parseInt(type.charAt(0)))}]`;
                }
                uniformsRef.current[name] = Object.assign({ type: glslType, isNeeded: false, value }, tempObject);
            }
        }
    };
    const processTextures = () => {
        const gl = glRef.current;
        if (!gl)
            return;
        if (textures && textures.length > 0) {
            uniformsRef.current[`${UNIFORM_CHANNELRESOLUTION}`] = {
                type: "vec3",
                isNeeded: false,
                arraySize: `[${textures.length}]`,
                value: [],
            };
            const texturePromisesArr = textures.map((texture, id) => {
                var _a;
                // Dynamically add textures uniforms.
                uniformsRef.current[`${UNIFORM_CHANNEL}${id}`] = {
                    type: "sampler2D",
                    isNeeded: false,
                };
                setupChannelRes(texture, id);
                texturesArrRef.current[id] = new Texture(gl);
                return (_a = texturesArrRef.current[id]) === null || _a === void 0 ? void 0 : _a.load(texture).then((t) => {
                    setupChannelRes(t, id);
                });
            });
            Promise.all(texturePromisesArr)
                .then(() => {
                if (onDoneLoadingTextures)
                    onDoneLoadingTextures();
            })
                .catch((e) => {
                onError === null || onError === void 0 ? void 0 : onError(e);
                if (onDoneLoadingTextures)
                    onDoneLoadingTextures();
            });
        }
        else if (onDoneLoadingTextures)
            onDoneLoadingTextures();
    };
    const preProcessFragment = (fragment) => {
        const isValidPrecision = PRECISIONS.includes(precision !== null && precision !== void 0 ? precision : "highp");
        const precisionString = `precision ${isValidPrecision ? precision : PRECISIONS[1]} float;\n`;
        if (!isValidPrecision) {
            onWarning === null || onWarning === void 0 ? void 0 : onWarning(log(`wrong precision type ${precision}, please make sure to pass one of a valid precision lowp, mediump, highp, by default you shader precision will be set to highp.`));
        }
        let fragmentShader = precisionString
            .concat(`#define DPR ${devicePixelRatio.toFixed(1)}\n`)
            .concat(fragment.replace(/texture\(/g, "texture2D("));
        for (const uniform of Object.keys(uniformsRef.current)) {
            if (fragment.includes(uniform)) {
                const u = uniformsRef.current[uniform];
                if (!u)
                    continue;
                fragmentShader = insertStringAtIndex(fragmentShader, `uniform ${u.type} ${uniform}${u.arraySize || ""}; \n`, fragmentShader.lastIndexOf(precisionString) + precisionString.length);
                u.isNeeded = true;
            }
        }
        const isShadertoy = fragment.includes("mainImage");
        if (isShadertoy)
            fragmentShader = fragmentShader.concat(FS_MAIN_SHADER);
        return fragmentShader;
    };
    const setUniforms = (timestamp) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        const gl = glRef.current;
        if (!gl || !shaderProgramRef.current)
            return;
        const delta = lastTimeRef.current
            ? (timestamp - lastTimeRef.current) / 1000
            : 0;
        lastTimeRef.current = timestamp;
        const propUniforms = propsUniformsRef.current;
        if (propUniforms) {
            for (const name of Object.keys(propUniforms)) {
                const currentUniform = propUniforms[name];
                if (!currentUniform)
                    continue;
                if ((_a = uniformsRef.current[name]) === null || _a === void 0 ? void 0 : _a.isNeeded) {
                    if (!shaderProgramRef.current)
                        return;
                    const customUniformLocation = gl.getUniformLocation(shaderProgramRef.current, name);
                    if (!customUniformLocation)
                        return;
                    processUniform(gl, customUniformLocation, currentUniform.type, currentUniform.value);
                }
            }
        }
        if ((_b = uniformsRef.current.iMouse) === null || _b === void 0 ? void 0 : _b.isNeeded) {
            const mouseUniform = gl.getUniformLocation(shaderProgramRef.current, UNIFORM_MOUSE);
            gl.uniform4fv(mouseUniform, uniformsRef.current.iMouse.value);
        }
        if ((_c = uniformsRef.current.iChannelResolution) === null || _c === void 0 ? void 0 : _c.isNeeded) {
            const channelResUniform = gl.getUniformLocation(shaderProgramRef.current, UNIFORM_CHANNELRESOLUTION);
            gl.uniform3fv(channelResUniform, uniformsRef.current.iChannelResolution.value);
        }
        if ((_d = uniformsRef.current.iDeviceOrientation) === null || _d === void 0 ? void 0 : _d.isNeeded) {
            const deviceOrientationUniform = gl.getUniformLocation(shaderProgramRef.current, UNIFORM_DEVICEORIENTATION);
            gl.uniform4fv(deviceOrientationUniform, uniformsRef.current.iDeviceOrientation.value);
        }
        if ((_e = uniformsRef.current.iTime) === null || _e === void 0 ? void 0 : _e.isNeeded) {
            const timeUniform = gl.getUniformLocation(shaderProgramRef.current, UNIFORM_TIME);
            gl.uniform1f(timeUniform, (timerRef.current += delta));
        }
        if ((_f = uniformsRef.current.iTimeDelta) === null || _f === void 0 ? void 0 : _f.isNeeded) {
            const timeDeltaUniform = gl.getUniformLocation(shaderProgramRef.current, UNIFORM_TIMEDELTA);
            gl.uniform1f(timeDeltaUniform, delta);
        }
        if ((_g = uniformsRef.current.iDate) === null || _g === void 0 ? void 0 : _g.isNeeded) {
            const d = new Date();
            const month = d.getMonth() + 1;
            const day = d.getDate();
            const year = d.getFullYear();
            const time = d.getHours() * 60 * 60 +
                d.getMinutes() * 60 +
                d.getSeconds() +
                d.getMilliseconds() * 0.001;
            const dateUniform = gl.getUniformLocation(shaderProgramRef.current, UNIFORM_DATE);
            gl.uniform4fv(dateUniform, [year, month, day, time]);
        }
        if ((_h = uniformsRef.current.iFrame) === null || _h === void 0 ? void 0 : _h.isNeeded) {
            const timeDeltaUniform = gl.getUniformLocation(shaderProgramRef.current, UNIFORM_FRAME);
            gl.uniform1i(timeDeltaUniform, uniformsRef.current.iFrame.value++);
        }
        if (texturesArrRef.current.length > 0) {
            for (let index = 0; index < texturesArrRef.current.length; index++) {
                const texture = texturesArrRef.current[index];
                if (!texture)
                    return;
                const { isVideo, _webglTexture, source, flipY, isLoaded } = texture;
                if (!isLoaded || !_webglTexture || !source)
                    return;
                if ((_j = uniformsRef.current[`iChannel${index}`]) === null || _j === void 0 ? void 0 : _j.isNeeded) {
                    if (!shaderProgramRef.current)
                        return;
                    const iChannel = gl.getUniformLocation(shaderProgramRef.current, `iChannel${index}`);
                    gl.activeTexture(gl.TEXTURE0 + index);
                    gl.bindTexture(gl.TEXTURE_2D, _webglTexture);
                    gl.uniform1i(iChannel, index);
                    if (isVideo) {
                        texture.updateTexture(_webglTexture, source, flipY);
                    }
                }
            }
        }
    };
    const drawScene = (timestamp) => {
        var _a, _b, _c, _d, _e, _f, _g;
        const gl = glRef.current;
        if (!gl)
            return;
        gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
        gl.bindBuffer(gl.ARRAY_BUFFER, squareVerticesBufferRef.current);
        gl.vertexAttribPointer((_a = vertexPositionAttributeRef.current) !== null && _a !== void 0 ? _a : 0, 3, gl.FLOAT, false, 0, 0);
        setUniforms(timestamp);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        const mouseValue = (_b = uniformsRef.current.iMouse) === null || _b === void 0 ? void 0 : _b.value;
        if (((_c = uniformsRef.current.iMouse) === null || _c === void 0 ? void 0 : _c.isNeeded) &&
            lerp !== 1 &&
            Array.isArray(mouseValue)) {
            const currentX = (_d = mouseValue[0]) !== null && _d !== void 0 ? _d : 0;
            const currentY = (_e = mouseValue[1]) !== null && _e !== void 0 ? _e : 0;
            mouseValue[0] = lerpVal(currentX, (_f = lastMouseArrRef.current[0]) !== null && _f !== void 0 ? _f : 0, lerp);
            mouseValue[1] = lerpVal(currentY, (_g = lastMouseArrRef.current[1]) !== null && _g !== void 0 ? _g : 0, lerp);
        }
        if (animateWhenNotVisibleRef.current || isVisibleRef.current) {
            animFrameIdRef.current = requestAnimationFrame(drawScene);
        }
    };
    const addEventListeners = () => {
        var _a, _b;
        const options = { passive: true };
        if (((_a = uniformsRef.current.iMouse) === null || _a === void 0 ? void 0 : _a.isNeeded) && canvasRef.current) {
            canvasRef.current.addEventListener("mousemove", mouseMove, options);
            canvasRef.current.addEventListener("mouseout", mouseUp, options);
            canvasRef.current.addEventListener("mouseup", mouseUp, options);
            canvasRef.current.addEventListener("mousedown", mouseDown, options);
            canvasRef.current.addEventListener("touchmove", mouseMove, options);
            canvasRef.current.addEventListener("touchend", mouseUp, options);
            canvasRef.current.addEventListener("touchstart", mouseDown, options);
        }
        if ((_b = uniformsRef.current.iDeviceOrientation) === null || _b === void 0 ? void 0 : _b.isNeeded) {
            window.addEventListener("deviceorientation", onDeviceOrientationChange, options);
        }
        if (canvasRef.current) {
            resizeObserverRef.current = new ResizeObserver(onResize);
            resizeObserverRef.current.observe(canvasRef.current);
            window.addEventListener("resize", onResize, options);
        }
    };
    const removeEventListeners = () => {
        var _a, _b;
        const options = { passive: true };
        if (((_a = uniformsRef.current.iMouse) === null || _a === void 0 ? void 0 : _a.isNeeded) && canvasRef.current) {
            canvasRef.current.removeEventListener("mousemove", mouseMove, options);
            canvasRef.current.removeEventListener("mouseout", mouseUp, options);
            canvasRef.current.removeEventListener("mouseup", mouseUp, options);
            canvasRef.current.removeEventListener("mousedown", mouseDown, options);
            canvasRef.current.removeEventListener("touchmove", mouseMove, options);
            canvasRef.current.removeEventListener("touchend", mouseUp, options);
            canvasRef.current.removeEventListener("touchstart", mouseDown, options);
        }
        if ((_b = uniformsRef.current.iDeviceOrientation) === null || _b === void 0 ? void 0 : _b.isNeeded) {
            window.removeEventListener("deviceorientation", onDeviceOrientationChange, options);
        }
        if (resizeObserverRef.current) {
            resizeObserverRef.current.disconnect();
            window.removeEventListener("resize", onResize, options);
        }
    };
    useEffect(() => {
        propsUniformsRef.current = propUniforms;
    }, [propUniforms]);
    useEffect(() => {
        animateWhenNotVisibleRef.current = animateWhenNotVisible;
        if (animateWhenNotVisible) {
            isVisibleRef.current = true;
        }
    }, [animateWhenNotVisible]);
    // Intersection Observer: pause animation when off-screen when animateWhenNotVisible is false
    useEffect(() => {
        if (animateWhenNotVisible || !canvasRef.current)
            return;
        const canvas = canvasRef.current;
        const observer = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                isVisibleRef.current = entry.isIntersecting;
                if (entry.isIntersecting) {
                    requestAnimationFrame(drawScene);
                }
            }
        }, { threshold: 0 });
        observer.observe(canvas);
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [animateWhenNotVisible]);
    // Main effect for initialization and cleanup
    useEffect(() => {
        const textures = texturesArrRef.current;
        function init() {
            initWebGL();
            const gl = glRef.current;
            if (gl && canvasRef.current) {
                gl.clearColor(...clearColor);
                gl.clearDepth(1.0);
                gl.enable(gl.DEPTH_TEST);
                gl.depthFunc(gl.LEQUAL);
                gl.viewport(0, 0, canvasRef.current.width, canvasRef.current.height);
                canvasRef.current.height = canvasRef.current.clientHeight;
                canvasRef.current.width = canvasRef.current.clientWidth;
                processCustomUniforms();
                processTextures();
                initShaders(preProcessFragment(fs || BASIC_FS), vs || BASIC_VS);
                initBuffers();
                requestAnimationFrame(drawScene);
                addEventListeners();
                onResize();
            }
        }
        initFrameIdRef.current = requestAnimationFrame(init);
        // Cleanup function
        return () => {
            var _a, _b, _c, _d;
            const gl = glRef.current;
            if (gl) {
                (_a = gl.getExtension("WEBGL_lose_context")) === null || _a === void 0 ? void 0 : _a.loseContext();
                gl.useProgram(null);
                gl.deleteProgram((_b = shaderProgramRef.current) !== null && _b !== void 0 ? _b : null);
                if (textures.length > 0) {
                    for (const texture of textures) {
                        gl.deleteTexture(texture._webglTexture);
                    }
                }
                shaderProgramRef.current = null;
            }
            removeEventListeners();
            cancelAnimationFrame((_c = initFrameIdRef.current) !== null && _c !== void 0 ? _c : 0);
            cancelAnimationFrame((_d = animFrameIdRef.current) !== null && _d !== void 0 ? _d : 0);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array to run only once on mount
    return (_jsx("canvas", Object.assign({ ref: canvasRef, style: Object.assign({ height: "100%", width: "100%" }, style) }, canvasProps)));
}
